// src/RiskCardForm.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Grid, Button, Fab, Zoom, Box, Link
} from '@mui/material';
import { Drawer } from '@mantine/core';
import { CheckCircle } from '@mui/icons-material';
import { useParams } from 'react-router-dom';

import AssessmentStepperV2     from './AssessmentStepperV2';
import AssessmentProgressIndicator from './components/AssessmentProgressIndicator';
import ContextAssessmentStepper from './components/ContextAssessmentStepper';
import Compliance              from './Compliance';
import SecurityRiskAssessment from './securityFlow/SecurityRiskAssessment';
import AssessmentCanvas        from './canvas/AssessmentCanvas.jsx';

import {
  fetchRiskCardData,
  updateAssessmentStep,
  updateAssessmentStatus,
   setAssessmentPosition,
 } from './redux/actions/riskActions';
import { fetchThemes } from './redux/actions/themeActions';
import { fetchContextData } from './redux/actions/contextActions';
import { RiskFormGenerator } from './redux/selectors/riskFormGenerator';
import contextForm from './config/contextForm.json';
import { positionToIndexes, indexesToPosition, positionToSearch, searchToPositionObject } from './utils/assessmentPosition';
import RiskFlowWrapperV2 from './riskFlow/RiskFlowWrapperV2';
// Convert camelCase or snake_case strings to Title Case
const toTitleCase = (str) => {
  if (!str) return '';
  return str
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]/g, ' ')
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

function RiskCardForm () {
  /*  ──────────────────────── params / nav ──────────────────────── */
  const { modelId, modelName: rawModelName, assessmentUuid } = useParams();
  const modelName = decodeURIComponent(rawModelName);
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const { search } = useLocation();
  //const params      = new URLSearchParams(search);
  //const useNewFlow  = params.get('newFlow') === '1';
  const useNewFlow  = true

  
  /*  ──────────────────────── selectors ─────────────────────────── */
  const assessmentStatus = useSelector(s => s.risks.models[modelId]?.assessmentStatus);
  const riskForm         = useSelector(RiskFormGenerator);
  const riskFormData     = useSelector(s => s.risks.models[modelId]?.riskFormData || {});
  const contextData      = useSelector(s => s.context.data[modelId] || {});
  const savedPosition    = useSelector(s => s.risks.models[modelId]?.position);

  /*  ──────────────────────── constants ─────────────────────────── */
  // Security assessment is now scenario-based; hide legacy sub-steps from the stepper UI
  const securitySteps = useMemo(() => [], []);
  const riskCardSteps = useMemo(
    () => useNewFlow
      ? []                                       // new holistic flow – no sub‑steps
      : ['riskCard', 'riskIdentification', 'riskEvaluation'],
    [useNewFlow]
  );

// Remove legacy security sub-step wizards (now handled inside the scenario-based flow)

const steps = useMemo(() => [
    { label: 'useCase',            sections: Object.keys(contextForm) },
    { label: 'safetyRisks', sections: [...Object.keys(riskForm)] },
    { label: 'securityAssessment', sections: securitySteps           },
    { label: 'compliance',         sections: []                      },
    { label: 'assessmentSummary',   sections: []                     }
  ], [riskForm, securitySteps]);

  
  /*  ───────────────────── navigation state ─────────────────────── */
  const [activeStep,         setActiveStep]         = useState(0);
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);
  const [activeRiskCardStep, setActiveRiskCardStep] = useState(0);
  const [activeSourceName,   setActiveSourceName]   = useState(null);
  const [activeSourceIndex,  setActiveSourceIndex]  = useState(0);
  const [firstLoad,          setFirstLoad]          = useState(true);
  const [stepperVisible,     setStepperVisible]     = useState(false);

  // const stepHeadings = {
  //   useCase            : ['Step 1- Use Case, Business & Model Details', 'Describe the use case, business purpose, limitations and essential model details'],
  //   safetyRisks        : ['Step 2- Safety Risk Assessment', 'Identify, evaluate and treat risks related to model safety.'],
  //   //actionPlan         : ['Step 3- Mitigation Plan', 'Identify and evaluate mitigating actions for model safety risks'],
  //   //residualRisk       : ['Step 4- Resudual Risk', 'Analyse and define residual risk after mitigations have been applied'],
  //   securityAssessment : ['Step 3- Security Assessment','Map threats, vulnerabilities and controls.'],
  //   compliance         : ['Step 4- Compliance Review','Cross-check your action plans with applicable regulations and your own policies.'],
  //   assessmentSummary  : ['Step 5- Assessment Summary', 'Generate and export the consolidated report.']
  // };
  
  // Note: stepHeadings now handled in AssessmentProgressIndicator
  const currentSectionKey = steps[activeStep].sections[activeSectionIndex] || '';
  const currentSectionLabel = toTitleCase(currentSectionKey);

  /*  ─────────────────────── narratives memo ────────────────────── */
  const narratives = useMemo(() => Object.fromEntries(
    Object.entries(contextData).map(([sec, d]) => [sec, d?.narrative ?? `No narrative for ${sec}`])
  ), [contextData]);

  /*  ─────────────────────── initial data fetch ─────────────────── */
  useEffect(() => {
    dispatch(fetchThemes());
    dispatch(fetchContextData(modelId, assessmentUuid));
    dispatch(fetchRiskCardData(modelId, assessmentUuid));

    if (!assessmentStatus || assessmentStatus === 'Not Started') {
      dispatch(updateAssessmentStatus(modelId, assessmentUuid, 'On-Going'));
    }
  }, [dispatch, modelId, assessmentUuid, assessmentStatus]);

  /*  ───────────────────── load cursor on mount ─────────────────── */
  useEffect(() => {
    if (!firstLoad) return;
    const urlPos = searchToPositionObject(search);
    const pos    = urlPos || savedPosition;
    if (pos) {
      const { majorIndex, sectionIndex, subIndex } =
        positionToIndexes(pos, steps, riskCardSteps);
      setActiveStep(majorIndex);
      setActiveSectionIndex(sectionIndex);
      setActiveRiskCardStep(subIndex);
    }
    setFirstLoad(false);
  }, [firstLoad, search, savedPosition, steps, riskCardSteps]);

  /*  ─────────────── keep Redux & URL in sync on move ───────────── */
  const persistPosition = useCallback(
    (m, s, r) => {
      const pos = indexesToPosition(m, s, r, steps, riskCardSteps);
      dispatch(setAssessmentPosition(modelId, assessmentUuid, pos));
      navigate({ search: positionToSearch(pos).toString() }, { replace: true });
    },
    [dispatch, navigate, modelId, assessmentUuid, steps, riskCardSteps]
  );

  useEffect(() => {
    if (!firstLoad)
      persistPosition(activeStep, activeSectionIndex, activeRiskCardStep);
  }, [activeStep, activeSectionIndex, activeRiskCardStep, firstLoad, persistPosition]);



  /*  ─────────────────────── Back / Next / FAB ───────────────────── */
  const nextMajorStep = activeStep < steps.length - 1
    ? steps[activeStep + 1].label
    : null;

  const handleNext = () => {
    console.log('[RiskCardForm] handleNext called:', {
      activeStep,
      activeSectionIndex,
      isSectionNA,
      sectionsLength: steps[activeStep].sections.length,
      timestamp: new Date().toISOString()
    });
    
    // Fast‑forward if current model‑risk section is marked N/A
    if (isSectionNA) {
      if (activeSectionIndex < steps[activeStep].sections.length - 1) {
        setActiveSectionIndex(prev => prev + 1);
        // Reset source tracking when moving to new area
        setActiveSourceName(null);
        setActiveSourceIndex(0);
      } else if (activeStep < steps.length - 1) {
        setActiveStep(prev => prev + 1);
        setActiveSectionIndex(0);
        setActiveRiskCardStep(0);
        // Reset source tracking when moving to new step
        setActiveSourceName(null);
        setActiveSourceIndex(0);
        dispatch(updateAssessmentStep(modelId, assessmentUuid, nextMajorStep));
      }
      return;
    }
    if (activeStep === 1 && activeRiskCardStep < riskCardSteps.length - 1) {
      setActiveRiskCardStep(prev => prev + 1);
    } else if (activeSectionIndex < steps[activeStep].sections.length - 1) {
      const nextSectionIndex = activeSectionIndex + 1;
      setActiveSectionIndex(nextSectionIndex);
      // Reset source tracking when moving to new area
      setActiveSourceName(null);
      setActiveSourceIndex(0);
      
      // Clear position/phase state for new area to ensure it starts fresh
      if (activeStep === 1) { // Safety risks step
        const nextArea = steps[activeStep].sections[nextSectionIndex];
        dispatch(setAssessmentPosition(modelId, assessmentUuid, {
          majorStep: 'safetyRisks',
          section: nextArea,
          subStep: undefined,
          phase: undefined,
          sourceName: undefined,
          riskName: undefined
        }));
      }
    } else if (activeStep < steps.length - 1) {
      setActiveStep(prev => prev + 1);
      setActiveSectionIndex(0);
      setActiveRiskCardStep(0);
      // Reset source tracking when moving to new step
      setActiveSourceName(null);
      setActiveSourceIndex(0);
      dispatch(updateAssessmentStep(modelId, assessmentUuid, nextMajorStep));
    }
  };


    const handleBack = () => {
    if (activeStep === 1 && activeRiskCardStep > 0) {
      setActiveRiskCardStep(prev => prev - 1);
    } else if (activeSectionIndex > 0) {
      setActiveSectionIndex(prev => prev - 1);
    } else if (activeStep > 0) {
      setActiveStep(prev => prev - 1);
      setActiveSectionIndex(steps[activeStep - 1].sections.length - 1);
      setActiveRiskCardStep(riskCardSteps.length - 1);
    }
  };

  const fabClick = async () => {
    // Still inside the assessment flow → advance to the next major step
    if (activeStep < steps.length - 1) {
      setActiveStep(prev => prev + 1);
      setActiveSectionIndex(0);
      setActiveRiskCardStep(0);
      dispatch(updateAssessmentStep(modelId, assessmentUuid, nextMajorStep));
      return;
    }

    /* ---- Assessment is finished → mark as Completed ---- */
    try {
      // 1) update Redux state immediately
      dispatch(updateAssessmentStatus(modelId, assessmentUuid, 'Completed'));

      // 2) persist status on the back‑end
      await fetch('/.netlify/functions/manageAssessment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          modelId,
          assessmentUuid,
          operation: 'update',
          assessmentStatus: 'Completed'
        })
      });
    } catch (err) {
      /* eslint-disable no-console */
      console.error('Error completing assessment:', err);
      /* eslint-enable */
    }

    // 3) redirect user to the Risk Reports overview
    navigate('/risk-assessments');
  };

  const isLastSection =
    (steps[activeStep].sections.length === 0) ||
    (activeSectionIndex === steps[activeStep].sections.length - 1);

  const currentSectionName = steps[activeStep].sections[activeSectionIndex];
  const isSectionNA = riskFormData[currentSectionName]?.status === 'NA';
 
  /*  ────────────────────────── render ──────────────────────────── */
  return (
    <Box sx={{ backgroundColor: '#F9FAFB', minHeight: '100vh', width: '100%' }}>
      <Grid container spacing={0}>
        {/* ─────────── Main Content Area - Full Width ─────────── */}
        <Grid
          item
          xs={12}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            p: 0,
          }}
        >

          {/* Parent wrapper with no background */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              backgroundColor: 'transparent',
            }}
          >
            {/* ─── (1) Progress Indicator - WHITE BACKGROUND ─── */}
            <Box
              sx={{
                backgroundColor: '#FFFFFF',
                borderBottom: '1px solid #E5E7EB',
                px: 6, // Match RiskFlow px="xl" (48px)
                py: 2,
              }}
            >
              <AssessmentProgressIndicator
                activeStep={activeStep}
                activeSectionIndex={activeSectionIndex}
                activeSourceIndex={activeSourceIndex}
                activeSourceName={activeSourceName}
                steps={steps}
                totalSteps={steps.length}
                riskFormData={riskFormData}
                contextData={contextData}
                modelName={modelName}
                modelId={modelId}
                assessmentUuid={assessmentUuid}
                onMenuClick={() => setStepperVisible(true)}
                variant="default"
              />
            </Box>

            {/* ─── (2) Content Box - GRAY BACKGROUND ─── */}
            <Box
              sx={{
                flex: 1,
                p: 0,
                backgroundColor: '#F9FAFB',
                overflowY: 'auto',
              }}
            >
              {/* Each component renders directly in the grey area */}
              {activeStep === 0 && (
                <ContextAssessmentStepper
                  modelId={modelId}
                  assessmentUuid={assessmentUuid}
                />
              )}
            
        {activeStep === 1 && (

                  <RiskFlowWrapperV2
                    key={currentSectionKey}         // Force remount when area changes
                    modelId={modelId}
                      assessmentUuid={assessmentUuid}
                      riskArea={currentSectionKey}    // canonical key, e.g. "Misinformation"
                      riskAreaLabel={currentSectionLabel}
                      narratives={narratives}
                      savedPosition={savedPosition}   // Pass saved position for state restoration
                      onAreaDone={handleNext}         // same callback stepper already uses
                      onSourceChange={(sourceName, sourceIndex) => {
                        setActiveSourceName(sourceName);
                        setActiveSourceIndex(sourceIndex);
                      }}
                    />
                    )}
                

   
              {activeStep === 2 && (
                <SecurityRiskAssessment
                  modelId={modelId}
                  assessmentUuid={assessmentUuid}
                />
              )}
              {activeStep === 3 && (
                <Compliance
                  modelId={modelId}
                  assessmentUuid={assessmentUuid}
                  riskFormData={riskFormData}
                  narratives={narratives}
                />
              )}
              {activeStep === 4 && (
                <AssessmentCanvas
                  modelId={modelId}
                  assessmentUuid={assessmentUuid}
                />
              )}
            </Box>

       
                       {/* ─── (3) Navigation Buttons Box - WHITE BACKGROUND with improved spacing ─── */}
                       <Box
              sx={{
                backgroundColor: '#FFFFFF',
                borderTop: '1px solid #E5E7EB',
                px: 6, // Match RiskFlow px="xl" (48px) for consistency
                py: 3,
              }}
            >
              <Grid container justifyContent="space-between" alignItems="center">
                {/* Back link — black styling */}
                <Link
                  component="button"
                  underline="none"
                  onClick={handleBack}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    color: '#111827',
                    textTransform: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                 
                  Back
                </Link>

                {/* Next / Finish */}
                <Box>
                {isLastSection && activeStep === steps.length - 1 ? (

                    <Zoom in>
                      <Fab
                        variant="extended"
                        color="primary"
                        onClick={fabClick}
                        sx={{ minWidth: 140 }}
                      >
                        <CheckCircle sx={{ mr: 1 }} />
                        Finish
                      </Fab>
                    </Zoom>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      sx={{
                        minWidth: 120,
                        bgcolor: '#111827',
                        textTransform: 'none',
                        '&:hover': { bgcolor: '#0f172a' },
                      }}
                    >
                      Next
                      
                    </Button>
                  )}
                </Box>
              </Grid>
            </Box>
               </Box>
            </Grid>  
        </Grid>
        
        {/* ─────────── Assessment Stepper Drawer ─────────── */}
        <Drawer
          opened={stepperVisible}
          onClose={() => setStepperVisible(false)}
          position="left"
          size="md"
          title="Assessment Navigation"
          styles={{
            header: {
              backgroundColor: '#f8f9fa',
              borderBottom: '1px solid #e9ecef'
            },
            body: {
              padding: 0,
              backgroundColor: '#ffffff'
            }
          }}
        >
          <AssessmentStepperV2
            modelId={modelId}
            modelName={modelName}
            steps={steps}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            activeSectionIndex={activeSectionIndex}
            setActiveSectionIndex={setActiveSectionIndex}
            activeSourceName={activeSourceName}
            activeSourceIndex={activeSourceIndex}
          />
        </Drawer>
    </Box>  
  );
}

export default RiskCardForm;